import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { UserMultiSelect } from '@/components/UserMultiSelect';
import { getAllUsers, pingPresence, getPresence, leavePresence, getTeams, archiveResource } from '@/services/api';
import { UserAvatar, AvatarStack } from '@/components/UserAvatar';
import { toast } from 'sonner';
import {
  Plus, ArrowLeft, Trash2, Save, Edit3, X, ChevronRight,
  GripVertical, Settings2, Columns3, RotateCcw, Download, FileSpreadsheet, FileText, Users, Lock, Archive
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WIDTH_OPTIONS = [
  { value: 'xs', label: 'XS', px: 'w-[80px] min-w-[80px]' },
  { value: 'sm', label: 'S', px: 'w-[120px] min-w-[120px]' },
  { value: 'md', label: 'M', px: 'w-[180px] min-w-[180px]' },
  { value: 'lg', label: 'L', px: 'w-[260px] min-w-[260px]' },
  { value: 'xl', label: 'XL', px: 'w-[360px] min-w-[360px]' },
];

const getWidthClass = (w) => WIDTH_OPTIONS.find(o => o.value === w)?.px || WIDTH_OPTIONS[2].px;

// =================== CREATE DIALOG ===================

const CreateCustomChecklist = ({ token, onCreate, users, teams }) => {
  const [title, setTitle] = useState('');
  const [columns, setColumns] = useState([
    { col_id: `col_${Date.now()}`, label: '', width: 'md' },
  ]);
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [teamIds, setTeamIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const addCol = () => setColumns([...columns, { col_id: `col_${Date.now()}_${columns.length}`, label: '', width: 'md' }]);
  const removeCol = (i) => setColumns(columns.filter((_, idx) => idx !== i));
  const updateCol = (i, field, val) => {
    const c = [...columns];
    c[i] = { ...c[i], [field]: val };
    setColumns(c);
  };

  const handleCreate = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    const validCols = columns.filter(c => c.label.trim());
    if (!validCols.length) { toast.error('Add at least one column with a label'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/custom-checklists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: title.trim(), columns: validCols, assigned_to_ids: assigneeIds, team_ids: teamIds }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Create failed'); }
      const data = await r.json();
      toast.success('Custom checklist created');
      onCreate(data.checklist_id);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-foreground">Create Custom Checklist</h2>
      <p className="text-sm text-muted-foreground">Define a title and columns. All inputs accept text as-is (no formatting).</p>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Checklist Title</label>
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. People Present - Shift A" className="max-w-md" data-testid="cc-title-input" />
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block flex items-center gap-1">
          <Users className="h-3.5 w-3.5" /> Assign To <span className="text-muted-foreground/70">(optional)</span>
        </label>
        <div className="max-w-md">
          <UserMultiSelect
            users={users}
            selectedIds={assigneeIds}
            onChange={setAssigneeIds}
            placeholder="Select assignees..."
            testIdPrefix="cc-assignee"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block flex items-center gap-1">
          <Users className="h-3.5 w-3.5" /> Assign to teams <span className="text-muted-foreground/70">(optional — adds all current members)</span>
        </label>
        <div className="max-w-md flex flex-wrap gap-1.5">
          {(teams || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No teams available.</p>
          ) : teams.map(t => {
            const checked = teamIds.includes(t.team_id);
            return (
              <button
                key={t.team_id}
                type="button"
                onClick={() => setTeamIds(checked ? teamIds.filter(x => x !== t.team_id) : [...teamIds, t.team_id])}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors cursor-pointer ${checked ? 'bg-[#2D8C8C] border-[#2D8C8C] text-white' : 'border-muted text-muted-foreground hover:bg-muted/40'}`}
                data-testid={`cc-team-${t.team_id}`}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-semibold text-foreground">Columns ({columns.length})</label>
          <Button variant="outline" size="sm" onClick={addCol} data-testid="cc-add-col"><Plus className="h-4 w-4 mr-1" />Add Column</Button>
        </div>
        <div className="space-y-2">
          {columns.map((col, i) => (
            <div key={col.col_id} className="flex items-center gap-2 p-3 rounded-lg border border-muted/50 bg-muted/10" data-testid={`cc-col-${i}`}>
              <span className="text-xs text-muted-foreground w-6 text-center font-mono">{i + 1}</span>
              <Input
                value={col.label}
                onChange={e => updateCol(i, 'label', e.target.value)}
                placeholder="Column label (e.g. Name, Department...)"
                className="flex-1 h-9 text-sm"
                data-testid={`cc-col-label-${i}`}
              />
              <div className="flex items-center gap-1">
                {WIDTH_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => updateCol(i, 'width', opt.value)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                      col.width === opt.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }`}
                    data-testid={`cc-col-width-${i}-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {columns.length > 1 && (
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => removeCol(i)}>
                  <X className="h-3.5 w-3.5 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={handleCreate} disabled={saving} data-testid="cc-create-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Creating...' : 'Create Checklist'}
        </Button>
      </div>
    </div>
  );
};

// =================== DETAIL / TABLE VIEW ===================

const CustomChecklistDetail = ({ checklistId, onBack, token, isAdmin, users, user }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editCols, setEditCols] = useState([]);
  const [editRows, setEditRows] = useState([]);
  const [editAssigneeIds, setEditAssigneeIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  const editingRef = useRef(false);

  const isMasterAdmin = user?.role === 'master_admin' || user?.is_master_admin;
  const isCreator = data?.created_by === user?.user_id;
  const canEditStructure = isMasterAdmin || isCreator;
  const assigneeIds = (data?.assignees || []).map(a => a.user_id);
  const isAssignee = user && assigneeIds.includes(user.user_id);
  const canEditData = canEditStructure || isAssignee || isAdmin;

  useEffect(() => { editingRef.current = editing; }, [editing]);

  const applyServerData = useCallback((d) => {
    setData(d);
    // Only reset editing state if NOT mid-edit with unsaved changes
    if (!editingRef.current || !dirtyRef.current) {
      setEditTitle(d.title);
      setEditCols(d.columns.map(c => ({ ...c })));
      setEditRows(d.rows.map(r => ({ ...r, values: { ...r.values } })));
      setEditAssigneeIds((d.assignees || []).map(a => a.user_id));
      dirtyRef.current = false;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/custom-checklists/${checklistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      applyServerData(d);
    } catch { toast.error('Failed to load checklist'); }
  }, [checklistId, token, applyServerData]);

  useEffect(() => { load(); }, [load]);

  // Real-time polling every 4s (pauses while saving)
  useEffect(() => {
    const id = setInterval(() => {
      if (saving) return;
      fetch(`${API}/api/custom-checklists/${checklistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) applyServerData(d); })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [checklistId, token, saving, applyServerData]);

  // Presence — ping every 5s and fetch other viewers (+ per-cell focus)
  const [viewers, setViewers] = useState([]);
  const focusCellRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const ping = () => pingPresence('custom_checklist', checklistId, editingRef.current, focusCellRef.current).catch(() => {});
    const fetchViewers = () => getPresence('custom_checklist', checklistId, true)
      .then((list) => { if (alive) setViewers(list || []); })
      .catch(() => {});
    ping(); fetchViewers();
    const pingTimer = setInterval(ping, 5000);
    const viewTimer = setInterval(fetchViewers, 5000);
    const onUnload = () => {
      navigator.sendBeacon && leavePresence('custom_checklist', checklistId).catch(() => {});
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      alive = false;
      clearInterval(pingTimer);
      clearInterval(viewTimer);
      window.removeEventListener('beforeunload', onUnload);
      leavePresence('custom_checklist', checklistId).catch(() => {});
    };
  }, [checklistId]);

  // Broadcast cell focus immediately (don't wait for the 5s heartbeat)
  const setFocusCell = useCallback((cellKey) => {
    if (focusCellRef.current === cellKey) return;
    focusCellRef.current = cellKey;
    pingPresence('custom_checklist', checklistId, editingRef.current, cellKey).catch(() => {});
  }, [checklistId]);

  // Stable colour per user_id
  const peerByCell = useMemo(() => {
    const PALETTE = ['#2D8C8C', '#E06BB0', '#F29E4C', '#7C5CF4', '#2BAE66', '#D24B4B', '#4AA5F0', '#C3873E'];
    const colorFor = (uid) => {
      let h = 0; for (let i = 0; i < (uid || '').length; i++) h = (h * 31 + uid.charCodeAt(i)) | 0;
      return PALETTE[Math.abs(h) % PALETTE.length];
    };
    const map = {};
    for (const p of viewers) {
      if (p.focus_cell && !map[p.focus_cell]) {
        map[p.focus_cell] = { ...p, color: colorFor(p.user_id) };
      }
    }
    return map;
  }, [viewers]);

  const exportFile = async (format) => {
    try {
      const r = await fetch(`${API}/api/custom-checklists/export/${checklistId}/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'excel' ? 'xlsx' : format;
      const baseName = (data?.title || 'checklist').replace(/[\\/:*?"<>|]+/g, '').trim() || 'checklist';
      a.download = `${baseName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error('Export failed'); }
  };

  const startEdit = () => {
    if (!canEditData) return;
    setEditing(true);
    dirtyRef.current = false;
  };
  const cancelEdit = () => {
    setEditing(false);
    dirtyRef.current = false;
    if (data) {
      setEditTitle(data.title);
      setEditCols(data.columns.map(c => ({ ...c })));
      setEditRows(data.rows.map(r => ({ ...r, values: { ...r.values } })));
      setEditAssigneeIds((data.assignees || []).map(a => a.user_id));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { rows: editRows };
      if (canEditStructure) {
        body.title = editTitle;
        body.columns = editCols;
        body.assigned_to_ids = editAssigneeIds;
      }
      const r = await fetch(`${API}/api/custom-checklists/${checklistId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      const updated = await r.json();
      setData(updated);
      setEditing(false);
      dirtyRef.current = false;
      toast.success('Checklist saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const markDirty = () => { dirtyRef.current = true; };

  const addRow = () => {
    const newRow = {
      row_id: `row_${Date.now()}_${editRows.length}`,
      values: {},
    };
    editCols.forEach(c => { newRow.values[c.col_id] = ''; });
    setEditRows([...editRows, newRow]);
    markDirty();
  };

  const removeRow = (i) => { setEditRows(editRows.filter((_, idx) => idx !== i)); markDirty(); };
  const updateCell = (rowIdx, colId, val) => {
    const rows = [...editRows];
    rows[rowIdx] = { ...rows[rowIdx], values: { ...rows[rowIdx].values, [colId]: val } };
    setEditRows(rows);
    markDirty();
  };

  const updateColWidth = (colIdx, width) => {
    const cols = [...editCols];
    cols[colIdx] = { ...cols[colIdx], width };
    setEditCols(cols);
    markDirty();
  };

  const updateColLabel = (colIdx, label) => {
    const cols = [...editCols];
    cols[colIdx] = { ...cols[colIdx], label };
    setEditCols(cols);
    markDirty();
  };

  const addColumn = () => {
    const newCol = { col_id: `col_${Date.now()}`, label: 'New Column', width: 'md', order: editCols.length };
    setEditCols([...editCols, newCol]);
    setEditRows(editRows.map(r => ({ ...r, values: { ...r.values, [newCol.col_id]: '' } })));
    markDirty();
  };

  const removeColumn = (colIdx) => {
    const colId = editCols[colIdx].col_id;
    setEditCols(editCols.filter((_, i) => i !== colIdx));
    setEditRows(editRows.map(r => {
      const vals = { ...r.values };
      delete vals[colId];
      return { ...r, values: vals };
    }));
    markDirty();
  };

  if (!data) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const cols = editing ? editCols : data.columns;
  const rows = editing ? editRows : data.rows;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="cc-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1 min-w-0">
          {editing && canEditStructure ? (
            <Input value={editTitle} onChange={e => { setEditTitle(e.target.value); markDirty(); }} className="text-lg font-semibold h-10 max-w-md" data-testid="cc-edit-title" />
          ) : (
            <h2 className="text-xl font-semibold text-foreground" data-testid="cc-detail-title">{data.title}</h2>
          )}
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            <span>Created by {data.created_by_name} on {new Date(data.created_at).toLocaleDateString()}</span>
            <span>· {rows.length} row{rows.length !== 1 ? 's' : ''} · {cols.length} column{cols.length !== 1 ? 's' : ''}</span>
            {editing && !canEditStructure && (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 gap-1">
                <Lock className="h-3 w-3" /> Data entry only
              </Badge>
            )}
          </p>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            {viewers.length > 0 && (
              <div
                className="flex items-center gap-2 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50"
                title={viewers.map(v => `${v.user_name}${v.is_editing ? ' (editing)' : ''}`).join(', ')}
                data-testid="cc-presence-badge"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <AvatarStack
                  users={viewers.map(v => ({ user_id: v.user_id, name: v.user_name }))}
                  size="sm"
                  max={4}
                  data-testid="cc-presence-avatars"
                />
                <span className="text-xs font-medium text-emerald-700">
                  {viewers.length} viewing
                </span>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => exportFile('csv')} data-testid="cc-export-csv"><FileText className="h-4 w-4 mr-1.5" />CSV</Button>
            <Button variant="outline" size="sm" onClick={() => exportFile('excel')} data-testid="cc-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
            <Button variant="outline" size="sm" onClick={() => exportFile('pdf')} data-testid="cc-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
            {canEditStructure && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!window.confirm('Archive this custom checklist? It can be restored from the Archive page.')) return;
                  try {
                    await archiveResource('custom_checklist', checklistId);
                    toast.success('Checklist archived');
                    onBack && onBack();
                  } catch (e) {
                    toast.error('Failed to archive');
                  }
                }}
                data-testid="cc-archive-btn"
              >
                <Archive className="h-4 w-4 mr-1.5" />Archive
              </Button>
            )}
            {canEditData && <Button onClick={startEdit} data-testid="cc-edit-btn"><Edit3 className="h-4 w-4 mr-2" />{canEditStructure ? 'Edit' : 'Enter Data'}</Button>}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            {viewers.length > 0 && (
              <AvatarStack
                users={viewers.map(v => ({ user_id: v.user_id, name: v.user_name }))}
                size="sm"
                max={3}
                data-testid="cc-presence-editing"
              />
            )}
            <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="cc-save-btn"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        )}
      </div>

      {/* Assignees */}
      {editing && canEditStructure ? (
        <div className="p-3 rounded-lg border border-muted/50 bg-muted/10 space-y-2">
          <div className="text-xs font-semibold text-foreground flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> Assigned To
          </div>
          <div className="max-w-md">
            <UserMultiSelect
              users={users}
              selectedIds={editAssigneeIds}
              onChange={(ids) => { setEditAssigneeIds(ids); markDirty(); }}
              placeholder="Select assignees..."
              testIdPrefix="cc-edit-assignee"
            />
          </div>
        </div>
      ) : (
        Array.isArray(data.assignees) && data.assignees.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="cc-assignees-display">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Assigned to:</span>
            {data.assignees.map((a) => (
              <span key={a.user_id} className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full border bg-muted/30">
                <UserAvatar user={a} size="xs" />
                <span className="text-xs">{a.name}</span>
              </span>
            ))}
          </div>
        )
      )}

      {/* Column width controls (edit mode, structure editors only) */}
      {editing && canEditStructure && (
        <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground flex items-center gap-1"><Settings2 className="h-3.5 w-3.5" />Column Settings</span>
            <Button variant="outline" size="sm" onClick={addColumn} data-testid="cc-add-column"><Plus className="h-3.5 w-3.5 mr-1" />Add Column</Button>
          </div>
          <div className="space-y-1.5">
            {editCols.map((col, ci) => (
              <div key={col.col_id} className="flex items-center gap-2">
                <Input
                  value={col.label}
                  onChange={e => updateColLabel(ci, e.target.value)}
                  className="h-8 text-sm flex-1 max-w-[200px]"
                  placeholder="Column label"
                  data-testid={`cc-edit-col-label-${ci}`}
                />
                <div className="flex items-center gap-0.5">
                  {WIDTH_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => updateColWidth(ci, opt.value)}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors cursor-pointer ${
                        col.width === opt.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {editCols.length > 1 && (
                  <button onClick={() => removeColumn(ci)} className="text-destructive hover:text-destructive/80 cursor-pointer p-1">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-muted/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="cc-table">
            <thead>
              <tr className="bg-muted/30 border-b border-muted/50">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground w-[40px]">#</th>
                {cols.map(col => (
                  <th key={col.col_id} className={`px-3 py-2.5 text-left text-xs font-semibold text-foreground ${getWidthClass(col.width)}`}>
                    {col.label}
                  </th>
                ))}
                {editing && <th className="px-3 py-2.5 w-[50px]" />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={cols.length + 1 + (editing ? 1 : 0)} className="px-3 py-10 text-center text-sm text-muted-foreground">
                    {editing ? 'Click "Add Row" to start entering data' : 'No data yet'}
                  </td>
                </tr>
              ) : (
                rows.map((row, ri) => (
                  <tr key={row.row_id} className="border-b border-muted/30 hover:bg-muted/10 transition-colors" data-testid={`cc-row-${ri}`}>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground font-mono">{ri + 1}</td>
                    {cols.map(col => {
                      const cellKey = `row:${row.row_id}|col:${col.col_id}`;
                      const peer = peerByCell[cellKey];
                      const outlineStyle = peer ? { outline: `2px dashed ${peer.color}`, outlineOffset: '1px', borderRadius: 6 } : undefined;
                      return (
                        <td
                          key={col.col_id}
                          className={`px-2 py-1 relative ${getWidthClass(col.width)}`}
                          style={outlineStyle}
                          data-testid={`cc-cell-wrap-${ri}-${col.col_id}`}
                        >
                          {peer && (
                            <span
                              className="absolute -top-2 left-1 text-[9px] font-semibold px-1.5 py-0.5 rounded text-white z-10 pointer-events-none"
                              style={{ background: peer.color }}
                            >
                              {peer.user_name}
                            </span>
                          )}
                          {editing ? (
                            <input
                              type="text"
                              value={row.values[col.col_id] || ''}
                              onChange={e => updateCell(ri, col.col_id, e.target.value)}
                              onFocus={() => setFocusCell(cellKey)}
                              onBlur={() => setFocusCell(null)}
                              className={`${getWidthClass(col.width)} h-8 px-2 text-sm rounded-md border border-muted bg-background focus:border-primary/50 focus:outline-none`}
                              data-testid={`cc-cell-${ri}-${col.col_id}`}
                            />
                          ) : (
                            <span className="text-sm text-foreground break-all">{row.values[col.col_id] || ''}</span>
                          )}
                        </td>
                      );
                    })}
                    {editing && (
                      <td className="px-2 py-1">
                        <button onClick={() => removeRow(ri)} className="text-destructive/60 hover:text-destructive cursor-pointer p-1" data-testid={`cc-remove-row-${ri}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Button variant="outline" onClick={addRow} data-testid="cc-add-row">
          <Plus className="h-4 w-4 mr-2" />Add Row
        </Button>
      )}
    </div>
  );
};

// =================== LIST VIEW ===================

const CustomChecklistsList = ({ onSelect, onNew, token, isAdmin }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/custom-checklists`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setChecklists(await r.json());
    } catch { toast.error('Failed to load custom checklists'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!confirm('Delete this custom checklist?')) return;
    try {
      await fetch(`${API}/api/custom-checklists/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted'); load();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Custom Checklists</h2>
          <p className="text-sm text-muted-foreground mt-1">Free-form checklists with custom columns and data entry</p>
        </div>
        {isAdmin && (
          <Button onClick={onNew} data-testid="cc-new-btn"><Plus className="h-4 w-4 mr-2" />New Custom Checklist</Button>
        )}
      </div>

      {checklists.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Columns3 className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No custom checklists yet</p>
          <p className="text-sm mt-1">Create one with your own columns and fields</p>
        </div>
      ) : (
        <div className="space-y-3">
          {checklists.map(cl => (
            <div
              key={cl.checklist_id}
              onClick={() => onSelect(cl.checklist_id)}
              className="p-4 rounded-xl border border-muted/50 bg-card hover:border-primary/30 transition-all cursor-pointer group"
              data-testid={`cc-item-${cl.checklist_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center flex-shrink-0">
                  <Columns3 className="h-4 w-4 text-teal-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{cl.title}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    <span>{cl.columns?.length || 0} columns</span>
                    <span>{cl.rows?.length || 0} rows</span>
                    <span>by {cl.created_by_name}</span>
                    {Array.isArray(cl.assignees) && cl.assignees.length > 0 && (
                      <span className="flex items-center gap-1" data-testid={`cc-list-assignees-${cl.checklist_id}`}>
                        <Users className="h-3 w-3" />
                        {cl.assignees.length} assigned
                      </span>
                    )}
                    <span>{new Date(cl.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {isAdmin && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={(e) => handleDelete(cl.checklist_id, e)} data-testid={`cc-delete-${cl.checklist_id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =================== MAIN PAGE ===================

export const CustomChecklistPage = () => {
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const urlId = params.id || null;
  const startInCreate = new URLSearchParams(location.search).get('new') === '1';
  const [view, setView] = useState(urlId ? 'detail' : (startInCreate ? 'create' : 'list'));
  const [selectedId, setSelectedId] = useState(urlId);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const isAdmin = user?.role === 'admin' || user?.role === 'master_admin' || user?.is_master_admin;

  // Sync with URL
  useEffect(() => {
    if (urlId) {
      setSelectedId(urlId);
      setView('detail');
    } else if (view === 'detail') {
      setSelectedId(null);
      setView('list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  useEffect(() => {
    // Users + teams are needed to render assignees/multiselect; always fetch, server enforces admin for write ops
    getAllUsers().then(setUsers).catch(() => {});
    getTeams().then(setTeams).catch(() => {});
  }, []);

  const goToList = () => {
    setSelectedId(null);
    setView('list');
    navigate('/custom-checklists');
  };

  const goToDetail = (id) => {
    setSelectedId(id);
    setView('detail');
    navigate(`/custom-checklists/${id}`);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="custom-checklist-page">
        {view === 'create' ? (
          <>
            <Button variant="ghost" size="sm" onClick={goToList} className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-1" />Back to list
            </Button>
            <CreateCustomChecklist
              token={token}
              users={users}
              teams={teams}
              onCreate={(id) => goToDetail(id)}
            />
          </>
        ) : view === 'detail' && selectedId ? (
          <CustomChecklistDetail
            checklistId={selectedId}
            onBack={goToList}
            token={token}
            isAdmin={isAdmin}
            users={users}
            user={user}
          />
        ) : (
          <CustomChecklistsList
            onSelect={(id) => goToDetail(id)}
            onNew={() => setView('create')}
            token={token}
            isAdmin={isAdmin}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomChecklistPage;
